import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../hooks/GlobalProvider";
import {
  ProductCatalogue,
  ProductsSubCat,
  ProductVariants,
  Products,
} from "../../../hooks/APIHooks.js";

const AddProduct = () => {
  const { user } = useAuth();
  const [catalogues, setCatalogues] = useState([]);
  const [subCatalogues, setSubCatalogues] = useState([]);
  const [variants, setVariants] = useState([]);
  const [variantOptions, setVariantOptions] = useState({});
  const [isMultiVariant, setIsMultiVariant] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    catalogue_id: "",
    sub_catalogue_id: "",
    product_name: "",
    description: "",
    price: "",
    stock: "",
    sku: "",
    product_image: null,
    product_images: [],
    variant_ids: [],
    option_ids: [],
    variant_prices: [],
    variant_skus: [],
    stock_quantities: [],
    variant_stock_status: [],
  });

  useEffect(() => {
    fetchCatalogues();
    fetchVariants();
  }, []);

  const fetchCatalogues = async () => {
    try {
      const response = await axios.get(ProductCatalogue);
      setCatalogues(response.data);
    } catch (error) {
      console.error("Error fetching catalogues:", error);
    }
  };

  const fetchSubCatalogues = async (catalogueId) => {
    try {
      const response = await axios.get(
        `${ProductsSubCat}?catalogue_id=${catalogueId}`
      );
      setSubCatalogues(response.data);
    } catch (error) {
      console.error("Error fetching sub-catalogues:", error);
    }
  };

  const fetchVariants = async () => {
    try {
      const response = await axios.get(ProductVariants);
      setVariants(response.data);
      const optionsMap = {};
      response.data.forEach((variant) => {
        optionsMap[variant.variant_id] = variant.options || [];
      });
      setVariantOptions(optionsMap);
    } catch (error) {
      console.error("Error fetching variants:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (name === "catalogue_id") {
      setForm({
        ...form,
        catalogue_id: value,
        sub_catalogue_id: "",
      });
      if (value) {
        fetchSubCatalogues(value);
      } else {
        setSubCatalogues([]);
      }
    } else if (type === "file") {
      if (name === "product_images") {
        const selectedFiles = Array.from(files).slice(0, 5);
        setForm({ ...form, [name]: selectedFiles });
      } else {
        setForm({ ...form, [name]: files[0] });
      }
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleVariantChange = (index, e) => {
    const { name, value } = e.target;
    const updatedForm = { ...form };

    if (name === "variant_ids") {
      updatedForm.variant_ids[index] = value;
      updatedForm.option_ids[index] = "";
      updatedForm.variant_skus[index] = form.sku ? `${form.sku}-` : "";
    } else if (name === "option_ids") {
      updatedForm.option_ids[index] = value;

      const selectedVariantId = updatedForm.variant_ids[index];
      const selectedVariantOptions = variantOptions[selectedVariantId];

      if (selectedVariantOptions) {
        const selectedOption = selectedVariantOptions.find(
          (option) => option.option_id == value
        );
        if (selectedOption) {
          updatedForm.variant_skus[index] = form.sku
            ? `${form.sku}-${selectedOption.option_value}`
            : selectedOption.option_value;
        }
      }
    } else if (name === "variant_prices") {
      updatedForm.variant_prices[index] = value;
    } else if (name === "stock_quantities") {
      updatedForm.stock_quantities[index] = value;
    } else if (name === "variant_stock_status") {
      updatedForm.variant_stock_status[index] = value;
    } else if (name === "variant_skus") {
      updatedForm.variant_skus[index] = value;
    }

    setForm(updatedForm);
  };

  const addVariant = () => {
    setForm({
      ...form,
      variant_ids: [...form.variant_ids, ""],
      option_ids: [...form.option_ids, ""],
      variant_prices: [...form.variant_prices, ""],
      variant_skus: [...form.variant_skus, form.sku ? `${form.sku}-` : ""],
      stock_quantities: [...form.stock_quantities, ""],
      variant_stock_status: [...form.variant_stock_status, "in_stock"],
    });
  };

  const removeVariant = (index) => {
    setForm({
      ...form,
      variant_ids: form.variant_ids.filter((_, i) => i !== index),
      option_ids: form.option_ids.filter((_, i) => i !== index),
      variant_prices: form.variant_prices.filter((_, i) => i !== index),
      variant_skus: form.variant_skus.filter((_, i) => i !== index),
      stock_quantities: form.stock_quantities.filter((_, i) => i !== index),
      variant_stock_status: form.variant_stock_status.filter(
        (_, i) => i !== index
      ),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate required fields
    if (
      !form.catalogue_id ||
      !form.sub_catalogue_id ||
      !form.product_name ||
      !form.description
    ) {
      alert("Please fill in all required fields");
      setLoading(false);
      return;
    }

    if (!isMultiVariant && !form.price) {
      alert("Please enter product price");
      setLoading(false);
      return;
    }

    if (isMultiVariant && form.variant_ids.length === 0) {
      alert("Please add at least one variant");
      setLoading(false);
      return;
    }

    if (!form.product_image) {
      alert("Please upload a main product image");
      setLoading(false);
      return;
    }

    const formData = new FormData();

    // Append basic product data
    formData.append("catalogue_id", form.catalogue_id);
    formData.append("sub_catalogue_id", form.sub_catalogue_id);
    formData.append("product_name", form.product_name);
    formData.append("description", form.description);
    formData.append("sku", form.sku || "");

    if (!isMultiVariant) {
      formData.append("price", form.price);
      formData.append("stock", form.stock || 0);
    }

    // Append main product image
    if (form.product_image) {
      formData.append("product_image", form.product_image);
    }

    // Append additional images
    if (form.product_images && form.product_images.length > 0) {
      form.product_images.forEach((image, index) => {
        formData.append(`product_images[${index}]`, image);
      });
    }

    // Append variant data if multi-variant
    if (isMultiVariant) {
      form.variant_ids.forEach((variantId, index) => {
        formData.append("variant_ids[]", variantId);
        formData.append("option_ids[]", form.option_ids[index]);
        formData.append("variant_prices[]", form.variant_prices[index]);
        formData.append("variant_skus[]", form.variant_skus[index]);
        formData.append("stock_quantities[]", form.stock_quantities[index]);
        formData.append(
          "variant_stock_status[]",
          form.variant_stock_status[index]
        );
      });
    }

    try {
      const response = await axios.post(Products, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.message) {
        alert(response.data.message);
        resetForm();
      } else if (response.data.error) {
        alert("Failed to add product: " + response.data.error);
      } else {
        alert("Product added successfully!");
        resetForm();
      }
    } catch (error) {
      console.error("Error adding product:", error);
      alert(
        "Failed to add product: " +
          (error.response?.data?.error || error.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      catalogue_id: "",
      sub_catalogue_id: "",
      product_name: "",
      description: "",
      price: "",
      stock: "",
      sku: "",
      product_image: null,
      product_images: [],
      variant_ids: [],
      option_ids: [],
      variant_prices: [],
      variant_skus: [],
      stock_quantities: [],
      variant_stock_status: [],
    });
    setSubCatalogues([]);
    setIsMultiVariant(false);
    setActiveTab("general");
  };

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="loader"></div>
        </div>
      )}
      <h2 className="text-xl text-white font-bold mb-4 text-center bg-primary py-4">
        Add New Product
      </h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white shadow-md rounded-lg p-6"
        encType="multipart/form-data"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-700 font-bold mb-2 text-sm">
              Select Catalogue *
            </label>
            <select
              name="catalogue_id"
              value={form.catalogue_id}
              onChange={handleChange}
              className="border py-2 px-3 w-full mb-4 text-sm rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select Catalogue</option>
              {catalogues.map((catalogue) => (
                <option
                  key={catalogue.catalogue_id}
                  value={catalogue.catalogue_id}
                >
                  {catalogue.catalogue_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-gray-700 font-bold mb-2 text-sm">
              Select Sub-Catalogue *
            </label>
            <select
              name="sub_catalogue_id"
              value={form.sub_catalogue_id}
              onChange={handleChange}
              className="border py-2 px-3 w-full mb-4 text-sm rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={!form.catalogue_id}
            >
              <option value="">Select Sub-Catalogue</option>
              {subCatalogues.map((sub) => (
                <option key={sub.sub_catalogue_id} value={sub.sub_catalogue_id}>
                  {sub.sub_catalogue_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 font-bold mb-2">
            Product Name *
          </label>
          <input
            type="text"
            name="product_name"
            value={form.product_name}
            onChange={handleChange}
            className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 font-bold mb-2">
            Description *
          </label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows="4"
            className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          ></textarea>
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 font-bold mb-2 text-sm">
            Product Type
          </label>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => {
                setIsMultiVariant(false);
                setActiveTab("general");
              }}
              className={`flex-1 border p-3 rounded text-sm font-medium transition-colors ${
                !isMultiVariant
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Single Product
            </button>
            <button
              type="button"
              onClick={() => {
                setIsMultiVariant(true);
                setActiveTab("inventory");
              }}
              className={`flex-1 border p-3 rounded text-sm font-medium transition-colors ${
                isMultiVariant
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Multi Variants
            </button>
          </div>
        </div>

        <div className="mb-4">
          <nav className="flex border-b">
            {isMultiVariant ? (
              <>
                <button
                  type="button"
                  onClick={() => setActiveTab("inventory")}
                  className={`py-3 px-6 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === "inventory"
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Inventory
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("attributes")}
                  className={`py-3 px-6 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === "attributes"
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Variants
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setActiveTab("general")}
                  className={`py-3 px-6 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === "general"
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  General
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("inventory")}
                  className={`py-3 px-6 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === "inventory"
                      ? "border-primary text-primary"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Inventory
                </button>
              </>
            )}
          </nav>
        </div>

        {activeTab === "general" && !isMultiVariant && (
          <div className="mb-4">
            <label className="block text-gray-700 font-bold mb-2">
              Price (₹) *
            </label>
            <input
              type="number"
              name="price"
              value={form.price}
              onChange={handleChange}
              className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              min="0"
              step="0.01"
            />
          </div>
        )}

        {activeTab === "inventory" && (
          <div className="mb-4 space-y-4">
            <div>
              <label className="block text-gray-700 font-bold mb-2">SKU</label>
              <input
                type="text"
                name="sku"
                value={form.sku}
                onChange={handleChange}
                className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Product SKU"
              />
            </div>

            {!isMultiVariant && (
              <>
                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    Stock *
                  </label>
                  <input
                    type="number"
                    name="stock"
                    value={form.stock}
                    onChange={handleChange}
                    className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    min="0"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "attributes" && isMultiVariant && (
          <div className="mb-4">
            <h4 className="font-bold text-lg mb-4">Product Variants</h4>
            {form.variant_ids.map((_, index) => (
              <div
                key={index}
                className="border rounded-lg p-4 mb-4 bg-gray-50"
              >
                <div className="flex justify-between items-center mb-3">
                  <h5 className="font-semibold">Variant {index + 1}</h5>
                  {form.variant_ids.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeVariant(index)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                      Variant Type *
                    </label>
                    <select
                      name="variant_ids"
                      value={form.variant_ids[index]}
                      onChange={(e) => handleVariantChange(index, e)}
                      className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Variant</option>
                      {variants.map((variant) => (
                        <option
                          key={variant.variant_id}
                          value={variant.variant_id}
                        >
                          {variant.variant_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                      Variant Option *
                    </label>
                    <select
                      name="option_ids"
                      value={form.option_ids[index]}
                      onChange={(e) => handleVariantChange(index, e)}
                      className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={!form.variant_ids[index]}
                    >
                      <option value="">Select Option</option>
                      {form.variant_ids[index] &&
                        variantOptions[form.variant_ids[index]] &&
                        variantOptions[form.variant_ids[index]].map(
                          (option) => (
                            <option
                              key={option.option_id}
                              value={option.option_id}
                            >
                              {option.option_value}
                            </option>
                          )
                        )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                      Variant SKU *
                    </label>
                    <input
                      type="text"
                      name="variant_skus"
                      value={form.variant_skus[index]}
                      onChange={(e) => handleVariantChange(index, e)}
                      className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                      Variant Price (₹) *
                    </label>
                    <input
                      type="number"
                      name="variant_prices"
                      value={form.variant_prices[index]}
                      onChange={(e) => handleVariantChange(index, e)}
                      className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                      Stock Quantity *
                    </label>
                    <input
                      type="number"
                      name="stock_quantities"
                      value={form.stock_quantities[index]}
                      onChange={(e) => handleVariantChange(index, e)}
                      className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      min="0"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addVariant}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 transition-colors"
            >
              + Add Another Variant
            </button>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-gray-700 font-bold mb-2">
            Main Product Image *
          </label>
          <input
            type="file"
            name="product_image"
            onChange={handleChange}
            className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            accept="image/*"
            required
          />
        </div>

        <div className="mb-6">
          <label className="block text-gray-700 font-bold mb-2">
            Additional Product Images (Max 5)
          </label>
          <input
            type="file"
            name="product_images"
            onChange={handleChange}
            className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            multiple
            accept="image/*"
          />
          <p className="text-sm text-gray-600 mt-1">
            You can select up to 5 additional images
          </p>
          {form.product_images.length > 0 && (
            <div className="mt-2">
              <p className="text-sm text-green-600">
                {form.product_images.length} additional image(s) selected
              </p>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-primary text-white px-6 py-3 rounded font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Adding Product..." : "Add Product"}
        </button>
      </form>

      <style jsx>{`
        .loader {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #3498db;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default AddProduct;
